import { buildCarOfferNode, fuelTypeLabel, clampDescription, type PageMeta } from "@/lib/seo";
import { canonicalFor } from "@/lib/public-canonical-adapter";
import { countryCodeToLocale, getDistanceUnit } from "@/lib/api";
import { MARKETS } from "@/i18n/markets";
import { publicDisplayName } from "@workspace/booking-rules";

export interface PublicCar {
  id?: unknown; year?: unknown; make?: unknown; model?: unknown; submodel?: unknown;
  country_code?: unknown; city?: unknown; description?: unknown;
  currency_code?: unknown; rental_driving_active?: unknown; rental_chauffeured_active?: unknown;
  rental_event_active?: unknown; price_driving?: unknown; chauffeured_hourly_rate?: unknown;
  event_hourly_rate?: unknown; price_event?: unknown; transmission?: unknown;
  chauffeured_min_price_hourly_rate?:unknown;event_min_price_hourly_rate?:unknown;minimum_price?:unknown;
  body_type?: unknown; fuel_type?: unknown;
  public_unavailable?: boolean;
  body?: unknown; color?: unknown; engine?: unknown; passengers?: unknown;
  mileage?: unknown; is_left_hand_drive?: unknown; category?: unknown;
  reviews_rating?: unknown; reviews_count?: unknown;
}
export function publicCarTitle(car: PublicCar): string {
  return [car.year, car.make, car.model, car.submodel].filter(value => value != null && String(value).trim()).join(" ");
}
/** Existing market roots and make-filtered search, never invented landings. */
export function publicCarBreadcrumbs(car: PublicCar, canonical: string) {
  const country = String(car.country_code || "").trim().toUpperCase();
  const market = MARKETS.find(item => item.code === countryCodeToLocale(country) && item.code !== "global");
  const make = typeof car.make === "string" ? car.make.trim() : "";
  const name = publicCarTitle(car);
  // Unknown hierarchy is not permission to fabricate a country or make.
  if (!market || !make || !name) return [];
  const origin = new URL(canonical).origin;
  const makeQuery = new URLSearchParams({ country, make });
  return [
    { name: "Home", url: `${origin}/` },
    { name: market.label, url: `${origin}/${market.code}` },
    { name: make, url: `${origin}/${market.code}/search?${makeQuery}` },
    { name, url: canonical },
  ];
}
function publicCarOffers(car: PublicCar, canonical: string, preview = false) {
  if (car.public_unavailable) return null;
  return buildCarOfferNode({
    currencyCode: car.currency_code as string,
    rentalDrivingActive: car.rental_driving_active as boolean,
    rentalChauffeuredActive: car.rental_chauffeured_active as boolean,
    rentalEventActive: car.rental_event_active as boolean,
    priceDriving: car.price_driving as number,
    chauffeuredHourlyRate: car.chauffeured_hourly_rate as number,
    eventHourlyRate: car.event_hourly_rate as number,
    chauffeurMinimum:car.chauffeured_min_price_hourly_rate as number,eventMinimum:car.event_min_price_hourly_rate as number,minimumPrice:car.minimum_price as number,
    priceEvent: car.price_event as number,
    offerId: `${canonical}#offer`, url: canonical,
    preview,
  });
}
export function publicCarOffer(car: PublicCar) {
  const offer = publicCarOffers(car, "");
  if (!offer) return null;
  const selected = offer["@type"] === "AggregateOffer"
    ? (offer.offers as Record<string, unknown>[])[0]
    : offer;
  return selected as { price: number; priceCurrency: string; priceSpecification: { unitCode: string }; [key: string]: unknown };
}
export function publicCarMeta({ car, images, pathname, currentCanonical, brand, localizedTitle, reviews = [], preview = false, verifiedCanonical = false }: {
  car: PublicCar; images: { url: string; cdn_full?: string | null }[];
  pathname: string; currentCanonical: string; brand: string; localizedTitle: string;
  reviews?: { reviewerName?: string; rating?: number | null; createdAt?: string; reviewText?: string | null }[];
  preview?: boolean;
  verifiedCanonical?: boolean;
}): PageMeta {
  const canonical = verifiedCanonical || car.public_unavailable === true && /^\/car\//.test(pathname)
    ? currentCanonical : canonicalFor({ kind: "car", pathname, currentCanonical, evidence: car });
  const name = publicCarTitle(car);
  const breadcrumbs = publicCarBreadcrumbs(car, canonical);
  const title = String(car.country_code).toUpperCase() === "US"
    ? `${name} for Rent${car.city ? ` in ${car.city}` : ""}`
    : localizedTitle;
  const offer = publicCarOffers(car, canonical, preview);
  const fuel = fuelTypeLabel(car.fuel_type);
  const vehicle: Record<string, unknown> = {
    "@type": "Vehicle", "@id": `${canonical}#vehicle`, name, url: canonical,
    description: String(car.description || title),
    ...(car.year ? { vehicleModelDate: String(car.year) } : {}),
    ...(car.make ? { brand: { "@type": "Brand", name: car.make } } : {}),
    ...(car.model ? { model: car.model } : {}),
    ...(car.transmission ? { vehicleTransmission: car.transmission } : {}),
    ...(typeof car.body_type === "string" && car.body_type.trim() ? { bodyType: car.body_type } :
      typeof car.body === "string" && car.body.trim() && !/^\d+$/.test(car.body) ? { bodyType: car.body } : {}),
    ...(car.make ? { manufacturer: { "@type": "Organization", name: car.make } } : {}),
    ...(car.color ? { color: car.color } : {}),
    ...(car.engine ? { vehicleEngine: { "@type": "EngineSpecification", name: car.engine } } : {}),
    ...(car.passengers ? { seatingCapacity: car.passengers } : {}),
    ...(typeof car.mileage === "number" && car.mileage >= 0 ? { mileageFromOdometer: {
      "@type": "QuantitativeValue", value: car.mileage,
      unitCode: getDistanceUnit(String(car.country_code || "")) === "mile" ? "SMI" : "KMT",
    } } : {}),
    ...(typeof car.is_left_hand_drive === "boolean" ? { steeringPosition:
      car.is_left_hand_drive ? "https://schema.org/LeftHandDriving" : "https://schema.org/RightHandDriving" } : {}),
    ...(fuel ? { fuelType: fuel } : {}),
    image: images.map(image => image.cdn_full || image.url),
    ...(offer ? { offers: { "@id": `${canonical}#offer` }, potentialAction: {
      "@type": "ReserveAction", name: "Book this car",
      target: { "@type": "EntryPoint", urlTemplate: canonical,
        actionPlatform: ["https://schema.org/DesktopWebPlatform", "https://schema.org/MobileWebPlatform"] },
      result: { "@type": "Reservation", name },
    } } : {}),
  };
  return {
    title: `${title} | ${brand}`,
    // Unavailable legacy cars retain their indexed story, never a bookable Offer.
    // Country/language exclusions are independently owned by the route projection.
    noIndexFollow: false,
    description: clampDescription(String(car.description || title)),
    canonical, ogType: "product", ogImage: images[0]?.cdn_full || images[0]?.url,
    jsonLd: {
      "@context": "https://schema.org", "@graph": [
        vehicle,
        ...(offer ? [{ "@type": "Product", "@id": `${canonical}#product`, name, url: canonical,
          description: String(car.description || title).slice(0, 300),
          image: images.map(image => image.cdn_full || image.url),
          sku: String(car.id),
          ...(car.make ? { brand: { "@type": "Brand", name: car.make } } : {}),
          ...(typeof car.category === "string" && car.category.trim() ? {category:car.category} : {}),
          isRelatedTo: { "@id": `${canonical}#vehicle` },
          ...(typeof car.reviews_rating === "number" && Number.isFinite(car.reviews_rating) &&
            car.reviews_rating >= 1 && car.reviews_rating <= 5 &&
            typeof car.reviews_count === "number" && Number.isSafeInteger(car.reviews_count) && car.reviews_count > 0
            ? { aggregateRating: { "@type": "AggregateRating", ratingValue: car.reviews_rating,
              reviewCount: car.reviews_count, bestRating: 5, worstRating: 1 } } : {}),
          ...(reviews.some(review => review.reviewerName?.trim() && typeof review.rating === "number" &&
            review.rating >= 1 && review.rating <= 5) ? { review: reviews.filter(review =>
              review.reviewerName?.trim() && typeof review.rating === "number" && review.rating >= 1 && review.rating <= 5)
              .slice(0, 8).map(review => ({ "@type": "Review",
                reviewRating: { "@type": "Rating", ratingValue: review.rating, bestRating: 5, worstRating: 1 },
                author: { "@type": "Person", name: review.reviewerName },
                datePublished: review.createdAt, reviewBody: (review.reviewText || "").slice(0, 500),
              })) } : {}),
           offers: offer }] : []),
        ...(breadcrumbs.length ? [{
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((item, index) => ({
            "@type": "ListItem", position: index + 1, name: item.name, item: item.url,
          })),
        }] : []),
      ],
    },
  };
}

interface CollectionPresentation {
  first_name?: string | null; last_name?: string | null;
  quick_intro?: string | null; host_bio?: string | null;
  about?: string | null; car_story?: string | null;
}
export function publicCollectionName(profile: CollectionPresentation, fallbackLabel: string, sourceId: string): string {
  return publicDisplayName(profile)
    || `${fallbackLabel} ${sourceId}`;
}
export function publicCollectionAboutTexts(profile: CollectionPresentation): string[] {
  const normalize = (value: string) => value.replace(/\s+/g, " ").trim();
  const seen = new Set([normalize(profile.quick_intro || "")]);
  return [profile.host_bio, profile.about, profile.car_story].filter((value): value is string => {
    if (!value?.trim() || seen.has(normalize(value))) return false;
    seen.add(normalize(value));
    return true;
  });
}
export function publicCollectionMeta({ profile, sourceId, fallbackLabel, pathname, currentCanonical, brand }: {
  profile: CollectionPresentation; sourceId: string; fallbackLabel: string;
  pathname: string; currentCanonical: string; brand: string;
}): PageMeta {
  const name = publicCollectionName(profile, fallbackLabel, sourceId);
  const canonical = canonicalFor({ kind: "collection", pathname, currentCanonical, evidence: profile });
  const description = publicCollectionAboutTexts(profile)[0] || profile.quick_intro || "";
  return {
    title: `${name}’s vehicle collection | ${brand}`,
    description: description.trim().slice(0, 160), canonical,
    // Reviews are visible HTML only: a multi-car collection is not a single
    // item eligible for Review/AggregateRating structured data.
    jsonLd: {
      "@context": "https://schema.org", "@type": "CollectionPage",
      "@id": `${canonical}#collection`, url: canonical, name,
    },
  };
}
